import {
  PIPELINE_STEPS,
  boardColumnIdForStatus,
  getPipelineIndex,
} from './matterPipeline.js';
import { fingerprintForeignEntries } from './recordTabNotifications.js';

const SORT_STORAGE_KEY = 'demandjet.messagesInboxSort';
const FILTER_STORAGE_KEY = 'demandjet.messagesInboxFilter';
const SEEN_STORAGE_KEY = 'demandjet.messagesInboxSeen';

export const MESSAGES_SORT_OPTIONS = Object.freeze([
  { id: 'activity', label: 'Last activity' },
  { id: 'status', label: 'Status' },
  { id: 'unread', label: 'Unread first' },
]);

export const DEFAULT_MESSAGES_SORT = 'activity';
export const DEFAULT_MESSAGES_FILTER = 'all';

const STATUS_FILTER_IDS = new Set(PIPELINE_STEPS.map((step) => step.id));

export const MESSAGES_FILTER_OPTIONS = Object.freeze([
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  ...PIPELINE_STEPS.map((step) => ({
    id: step.id,
    label: step.shortLabel || step.label,
    group: 'Status',
  })),
]);

function storage() {
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

export function isValidMessagesSort(sortId) {
  return MESSAGES_SORT_OPTIONS.some((option) => option.id === sortId);
}

export function isValidMessagesFilter(filterId) {
  const id = String(filterId || '').trim();
  return id === 'all' || id === 'unread' || STATUS_FILTER_IDS.has(id);
}

export function loadMessagesSort() {
  const store = storage();
  if (!store) return DEFAULT_MESSAGES_SORT;
  try {
    const raw = store.getItem(SORT_STORAGE_KEY);
    const value = String(raw || '').trim();
    return isValidMessagesSort(value) ? value : DEFAULT_MESSAGES_SORT;
  } catch {
    return DEFAULT_MESSAGES_SORT;
  }
}

export function saveMessagesSort(sortId) {
  const next = isValidMessagesSort(sortId) ? sortId : DEFAULT_MESSAGES_SORT;
  const store = storage();
  if (store) {
    try {
      store.setItem(SORT_STORAGE_KEY, next);
    } catch {
      // Quota / private mode — ignore.
    }
  }
  return next;
}

export function loadMessagesFilter() {
  const store = storage();
  if (!store) return DEFAULT_MESSAGES_FILTER;
  try {
    const raw = store.getItem(FILTER_STORAGE_KEY);
    const value = String(raw || '').trim();
    return isValidMessagesFilter(value) ? value : DEFAULT_MESSAGES_FILTER;
  } catch {
    return DEFAULT_MESSAGES_FILTER;
  }
}

export function saveMessagesFilter(filterId) {
  const next = isValidMessagesFilter(filterId) ? filterId : DEFAULT_MESSAGES_FILTER;
  const store = storage();
  if (store) {
    try {
      store.setItem(FILTER_STORAGE_KEY, next);
    } catch {
      // Quota / private mode — ignore.
    }
  }
  return next;
}

export function loadInboxSeenMap() {
  const store = storage();
  if (!store) return {};
  try {
    const raw = store.getItem(SEEN_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

export function saveInboxSeenMap(map) {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(SEEN_STORAGE_KEY, JSON.stringify(map || {}));
  } catch {
    // Quota / private mode — ignore.
  }
}

export function conversationForeignSignature(thread, viewer = {}) {
  return fingerprintForeignEntries(thread?.messages || [], viewer);
}

/** Unread when attorney/teammate activity differs from the last-opened inbox signature. */
export function isConversationUnread(thread, viewer = {}, map = loadInboxSeenMap()) {
  const matterId = String(thread?.matterId || '');
  if (!matterId) return false;
  const current = conversationForeignSignature(thread, viewer);
  if (current === '0') return false;
  if (!Object.prototype.hasOwnProperty.call(map, matterId)) return true;
  return String(map[matterId] ?? '') !== current;
}

export function markConversationSeen(thread, viewer = {}, map = loadInboxSeenMap()) {
  const matterId = String(thread?.matterId || '');
  if (!matterId) return map;
  const signature = conversationForeignSignature(thread, viewer);
  const next = { ...map, [matterId]: String(signature ?? '0') };
  saveInboxSeenMap(next);
  return next;
}

export function matterAccountNumber(matter) {
  return String(matter?.External_Account_Number || '').trim();
}

export function matterInvoiceNumber(matter) {
  return String(matter?.External_Invoice_ID || matter?.Invoice_ID || '').trim();
}

function activityMs(thread) {
  return new Date(thread?.updatedAt || 0).getTime();
}

function compareActivity(a, b) {
  return activityMs(b) - activityMs(a);
}

function compareStatus(a, b) {
  const stageDiff =
    getPipelineIndex(a?.matter?.Status) - getPipelineIndex(b?.matter?.Status);
  if (stageDiff !== 0) return stageDiff;
  return compareActivity(a, b);
}

function compareUnread(a, b, viewer, map) {
  const aUnread = isConversationUnread(a, viewer, map) ? 0 : 1;
  const bUnread = isConversationUnread(b, viewer, map) ? 0 : 1;
  if (aUnread !== bUnread) return aUnread - bUnread;
  return compareActivity(a, b);
}

export function conversationMatchesFilter(
  thread,
  filterId = DEFAULT_MESSAGES_FILTER,
  { viewer = {}, seenMap = loadInboxSeenMap() } = {}
) {
  const filter = isValidMessagesFilter(filterId) ? filterId : DEFAULT_MESSAGES_FILTER;
  if (filter === 'all') return true;
  if (filter === 'unread') return isConversationUnread(thread, viewer, seenMap);
  return boardColumnIdForStatus(thread?.matter?.Status) === filter;
}

export function filterMessageConversations(
  threads = [],
  filterId = DEFAULT_MESSAGES_FILTER,
  { viewer = {}, seenMap = loadInboxSeenMap() } = {}
) {
  const list = Array.isArray(threads) ? threads : [];
  const filter = isValidMessagesFilter(filterId) ? filterId : DEFAULT_MESSAGES_FILTER;
  if (filter === 'all') return list;
  return list.filter((thread) =>
    conversationMatchesFilter(thread, filter, { viewer, seenMap })
  );
}

export function sortMessageConversations(
  threads = [],
  sortId = DEFAULT_MESSAGES_SORT,
  { viewer = {}, seenMap = loadInboxSeenMap() } = {}
) {
  const list = Array.isArray(threads) ? [...threads] : [];
  const sort = isValidMessagesSort(sortId) ? sortId : DEFAULT_MESSAGES_SORT;
  if (sort === 'status') {
    return list.sort(compareStatus);
  }
  if (sort === 'unread') {
    return list.sort((a, b) => compareUnread(a, b, viewer, seenMap));
  }
  return list.sort(compareActivity);
}
